import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { bookingsAPI, servicesAPI } from '../../services/api';
import { useBooking } from '../../contexts/BookingContext';
import '../../pages/BookingPage.css';

// Generate time slots in 15-minute intervals
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour <= 17; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      if (hour === 17 && minute > 30) break; // Stop at 5:30 PM
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayTime = new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      slots.push({ value: timeStr, label: displayTime });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const defaultValues = {
  guest_name: '',
  guest_email: '',
  guest_phone: '',
  vehicle_make: '',
  vehicle_model: '',
  vehicle_year: '',
  vehicle_rego: '',
  odometer_reading: '',
  preferred_date: '',
  preferred_time: '',
  customer_notes: '',
  service: '',
};

const BookingForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const { resetBooking } = useBooking();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ defaultValues });

  // Get today's date in Melbourne timezone
  const getTodayInMelbourne = () => {
    const now = new Date();
    // Convert to Melbourne time (Australia/Melbourne)
    const melbourneTime = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
    // Format as YYYY-MM-DD
    const year = melbourneTime.getFullYear();
    const month = String(melbourneTime.getMonth() + 1).padStart(2, '0');
    const day = String(melbourneTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    let isMounted = true;

    const loadServices = async () => {
      try {
        // Use getAllServices to get all services without pagination
        const response = await servicesAPI.getAllServices();
        const results = Array.isArray(response.data) ? response.data : [];

        if (isMounted) {
          setServiceOptions(results);
          console.log('Loaded services for booking:', results.length);
        }
      } catch (error) {
        console.error('Failed to fetch services list', error);
        console.error('Error details:', error.response?.data || error.message);
      } finally {
        if (isMounted) {
          setServicesLoading(false);
        }
      }
    };

    loadServices();

    return () => {
      isMounted = false;
    };
  }, []);

  // Don't auto-select first service - let user choose
  // useEffect(() => {
  //   const firstService = serviceOptions[0];
  //   if (firstService && !getValues('service')) {
  //     setValue('service', firstService.id);
  //   }
  // }, [serviceOptions, getValues, setValue]);

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      const response = await bookingsAPI.createBooking(formData);
      toast.success(`Booking confirmed! Reference: ${response.data.booking_reference}`);
      reset({ ...defaultValues, service: serviceOptions[0]?.id || '' });
      resetBooking();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data) {
        const errors = error.response.data;

        // Display specific field errors
        if (typeof errors === 'object') {
          Object.keys(errors).forEach(key => {
            const message = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
            toast.error(message);
          });
        } else {
          toast.error(errors);
        }
      } else {
        toast.error('Failed to create booking. Please try again.');
      }
      console.error('Booking error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className="booking-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="form-section">
        <h3>Your Details</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="guest_name">Full Name *</label>
            <input
              id="guest_name"
              type="text"
              {...register('guest_name', { required: 'Name is required' })}
              className={errors.guest_name ? 'error' : ''}
            />
            {errors.guest_name && <span className="error-message">{errors.guest_name.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="guest_email">Email *</label>
            <input
              id="guest_email"
              type="email"
              {...register('guest_email', { required: 'Email is required' })}
              className={errors.guest_email ? 'error' : ''}
            />
            {errors.guest_email && <span className="error-message">{errors.guest_email.message}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="guest_phone">Phone *</label>
          <input
            id="guest_phone"
            type="tel"
            {...register('guest_phone', { required: 'Phone is required' })}
            className={errors.guest_phone ? 'error' : ''}
          />
          {errors.guest_phone && <span className="error-message">{errors.guest_phone.message}</span>}
        </div>
      </div>

      <div className="form-section">
        <h3>Vehicle Details</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="vehicle_make">Make *</label>
            <input
              id="vehicle_make"
              type="text"
              {...register('vehicle_make', { required: 'Vehicle make is required' })}
              className={errors.vehicle_make ? 'error' : ''}
            />
            {errors.vehicle_make && <span className="error-message">{errors.vehicle_make.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="vehicle_model">Model *</label>
            <input
              id="vehicle_model"
              type="text"
              {...register('vehicle_model', { required: 'Vehicle model is required' })}
              className={errors.vehicle_model ? 'error' : ''}
            />
            {errors.vehicle_model && <span className="error-message">{errors.vehicle_model.message}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="vehicle_year">Year *</label>
            <input
              id="vehicle_year"
              type="number"
              {...register('vehicle_year', { required: 'Year is required' })}
              className={errors.vehicle_year ? 'error' : ''}
            />
            {errors.vehicle_year && <span className="error-message">{errors.vehicle_year.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="vehicle_rego">Registration</label>
            <input id="vehicle_rego" type="text" {...register('vehicle_rego')} />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="odometer_reading">Odometer Reading (km)</label>
          <input id="odometer_reading" type="number" {...register('odometer_reading')} />
        </div>
      </div>

      <div className="form-section">
        <h3>Booking Preferences</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="service">Service *</label>
            <select
              id="service"
              {...register('service', { required: 'Please select a service' })}
              disabled={servicesLoading}
              className={errors.service ? 'error' : ''}
            >
              <option value="">Select a service</option>
              {serviceOptions.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
            {servicesLoading && <span className="form-helper">Loading services...</span>}
            {!servicesLoading && serviceOptions.length === 0 && (
              <span className="form-helper">No services available yet. Please contact us directly.</span>
            )}
            {errors.service && <span className="error-message">{errors.service.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="preferred_date">Preferred Date *</label>
            <input
              id="preferred_date"
              type="date"
              min={getTodayInMelbourne()}
              {...register('preferred_date', { required: 'Preferred date is required' })}
              className={errors.preferred_date ? 'error' : ''}
            />
            {errors.preferred_date && <span className="error-message">{errors.preferred_date.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="preferred_time">Preferred Time *</label>
            <select
              id="preferred_time"
              {...register('preferred_time', { required: 'Preferred time is required' })}
              className={errors.preferred_time ? 'error' : ''}
            >
              <option value="">Select a time</option>
              {TIME_SLOTS.map((slot) => (
                <option key={slot.value} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
            {errors.preferred_time && <span className="error-message">{errors.preferred_time.message}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="customer_notes">Additional Details</label>
          <textarea
            id="customer_notes"
            rows="4"
            {...register('customer_notes')}
            placeholder="Let us know how we can help"
          />
        </div>
      </div>

      <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
        {loading ? 'Submitting...' : 'Book Appointment'}
      </button>
    </motion.form>
  );
};

export default BookingForm;
